import { useMemo } from 'react';

import { NumberField, NumberFieldProps } from '@components/number-field';
import { getScopedTranslate } from '@locale/translate';
import { hasNumericValue } from '@utils/numbers';

import { translations as formTranslations } from './locale';
import { FormFieldProps, FormValidatorType } from './types';
import { useFormField } from './use-form-field';

export type FormNumberFieldProps<T = unknown> = FormFieldProps<NumberFieldProps, T> & {
  disableNegative?: boolean;
};

export function FormNumberField<T>(props: FormNumberFieldProps<T>) {
  const { t } = getScopedTranslate(formTranslations);
  const { validators, min, max, disableNegative = false, isDecimal = false, ...rest } = props;

  const validateMinNumber = (value: number | undefined | null) => {
    if (!hasNumericValue(min) || !hasNumericValue(value)) return true;
    return value >= min ? true : t('min-number', { min: min as number });
  };

  const validateMaxNumber = (value: number | undefined | null) => {
    if (!hasNumericValue(max) || !hasNumericValue(value)) return true;
    return value <= max ? true : t('max-number', { max: max as number });
  };

  const validateNoNegative = (value: number | undefined | null) => {
    if (!disableNegative) return true;
    if (!hasNumericValue(value)) return true;
    return value >= 0 ? true : t('no-negative-number');
  };

  const validateIsNumber = (value: number | undefined | null) => {
    if (!hasNumericValue(value)) return true;
    if (typeof value !== 'number' || Number.isNaN(value)) {
      const key = isDecimal ? 'decimal' : 'number';
      return t(key as any);
    }
    return true;
  };

  const numberValidators: FormValidatorType[] = useMemo(
    () => [
      ...(validators ?? []),
      validateIsNumber,
      validateMinNumber,
      validateMaxNumber,
      validateNoNegative,
    ],
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(validators)],
  );
  const formFieldProps = useFormField({ ...rest, validators: numberValidators });

  const {
    validators: _ignoredValidators,
    disableNegative: _ignoredDisableNegative,
    ...numberFieldProps
  } = props;

  return <NumberField {...numberFieldProps} {...formFieldProps} />;
}
